import React, { useCallback, useMemo, useState } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'
import Dayjs from 'dayjs'

import Box from 'components_new/atoms/Box'
import DatePicker from 'components_new/atoms/DatePicker'

import Node, { InputType } from 'components_new/molecules/Node'
import Caption from 'components_new/molecules/Node/Caption'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import FunctionSelect from 'components_new/molecules/Node/FunctionSelect'
import { BOARD } from 'components_new/molecules/Node/accents'

import NodeType from 'types/transformation/enums/NodeType'
import DataType from 'types/transformation/enums/DataType'
import TimeUnit from 'types/transformation/enums/TimeUnit'
import { TODAY } from 'types/transformation/enums/filter/Type'
import {
  TimeDifferenceNode,
  Node as TNode
} from 'types/transformation/types/nodes'

import {
  pruneRef,
  useInputSchema,
  useIsInvalid,
  usePruneRefs,
  useColumnOrigins
} from '../hooks'

const SPECIFIC = 'INPUT_VALUE'
const ACCENT = '#3B82F6'

const TIME_UNITS = [
  { id: TimeUnit.MINUTES, name: 'Minuter' },
  { id: TimeUnit.HOURS, name: 'Timmar' },
  { id: TimeUnit.DAYS, name: 'Dagar' },
  { id: TimeUnit.YEARS, name: 'År' }
]

const TILL_KIND_OPTIONS = [
  { id: TODAY, name: 'Idag' },
  { id: SPECIFIC, name: 'Specifikt datum' }
]

interface TimeDifferenceNodeProps {
  id: string
  data: TimeDifferenceNode
  selected: boolean
}

/** Two-segment pill toggle matching the board's segmented-toggle style. */
const SegmentedToggle = (props: {
  value: string
  options: { id: string; label: string }[]
  onChange: (id: string) => void
}) => (
  <Box
    sx={{
      backgroundColor: BOARD.chipBackground,
      borderRadius: '10px',
      display: 'flex',
      gap: '2px',
      p: '3px'
    }}
  >
    {props.options.map((option) => {
      const active = props.value === option.id

      return (
        <Box
          key={option.id}
          role="button"
          onClick={() => props.onChange(option.id)}
          sx={{
            backgroundColor: active ? 'common.white' : 'transparent',
            borderRadius: '8px',
            boxShadow: active ? '0 1px 2px rgba(16, 24, 40, 0.08)' : 'none',
            color: active ? BOARD.textPrimary : BOARD.textMuted,
            cursor: 'pointer',
            flex: 1,
            fontFamily: BOARD.fontDisplay,
            fontSize: '13px',
            fontWeight: 600,
            py: '8px',
            textAlign: 'center'
          }}
        >
          {option.label}
        </Box>
      )
    })}
  </Box>
)

const TimeDifference = (props: TimeDifferenceNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  const [useColumn, setUseColumn] = useState(!!data.right_column)
  const [nameFocused, setNameFocused] = useState(false)
  const [valueType, setValueType] = useState(
    data.value === TODAY ? TODAY : SPECIFIC
  )

  // Time difference operates on date-like columns (strings can hold dates too).
  const dateColumns = useMemo(
    () =>
      inputSchema.filter(
        (col) =>
          col.type === DataType.DATE ||
          col.type === DataType.TIMESTAMP ||
          col.type === DataType.STRING
      ),
    [inputSchema]
  )

  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      dateColumns.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [dateColumns, originByNode]
  )

  // input schema has changed, validate references
  usePruneRefs<TimeDifferenceNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      left_column: pruneRef(nodeData.left_column, dateColumns),
      right_column: pruneRef(nodeData.right_column, dateColumns)
    }),
    [dateColumns]
  )

  const patch = useCallback(
    (partial: Partial<TimeDifferenceNode>) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: { ...(node.data as TimeDifferenceNode), ...partial }
              }
            : node
        )
      )
    },
    [setNodes, id]
  )

  const handleChangeNewColumnName = useCallback(
    (name: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) return node

          const newNodeData = cloneDeep(node.data) as TimeDifferenceNode

          newNodeData.new_column.name = name

          return { ...node, data: newNodeData }
        })
      )
    },
    [setNodes, id]
  )

  const handleToggleUseColumn = useCallback(
    (kind: string) => {
      const nextUseColumn = kind === 'column'

      setUseColumn(nextUseColumn)
      patch({
        right_column: nextUseColumn ? data.right_column : null,
        value: nextUseColumn ? null : data.value
      })
    },
    [patch, data.right_column, data.value]
  )

  const handleChangeValueType = useCallback(
    (type: string) => {
      setValueType(type)
      patch({ value: type === TODAY ? TODAY : null })
    },
    [patch]
  )

  return (
    <Node
      error={isInvalid}
      icon="TimerOutlined"
      inputType={InputType.SINGLE}
      title={'Time difference'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.TIME_DIFFERENCE}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          width: '304px'
        }}
      >
        <input
          className="nodrag ignore-locked-mode"
          onBlur={() => setNameFocused(false)}
          onChange={(event) => handleChangeNewColumnName(event.target.value)}
          onFocus={() => setNameFocused(true)}
          placeholder="Attributnamn"
          value={data.new_column.name}
          style={{
            background: nameFocused ? '#ffffff' : BOARD.rowBackground,
            border: `1px solid ${nameFocused ? ACCENT : 'transparent'}`,
            borderRadius: '10px',
            boxSizing: 'border-box',
            color: BOARD.textPrimary,
            fontFamily: BOARD.fontBody,
            fontSize: '13.5px',
            fontWeight: 500,
            outline: 'none',
            padding: '9px 11px',
            width: '100%'
          }}
        />

        <Box sx={{ mt: 0.5 }}>
          <Caption>Från</Caption>
        </Box>
        <FieldSelect
          value={data.left_column}
          options={fieldOptions}
          onChange={(columnId) => patch({ left_column: columnId })}
          placeholder="Välj attribut"
        />

        <Box sx={{ mt: 0.5 }}>
          <Caption>Till</Caption>
        </Box>
        <SegmentedToggle
          value={useColumn ? 'column' : 'date'}
          options={[
            { id: 'column', label: 'Kolumn' },
            { id: 'date', label: 'Datum' }
          ]}
          onChange={handleToggleUseColumn}
        />
        {useColumn ? (
          <FieldSelect
            value={data.right_column}
            options={fieldOptions}
            onChange={(columnId) => patch({ right_column: columnId })}
            placeholder="Välj attribut"
          />
        ) : (
          <>
            <FunctionSelect
              value={valueType}
              options={TILL_KIND_OPTIONS}
              onChange={handleChangeValueType}
              sx={{ width: '100%' }}
            />
            {valueType === SPECIFIC ? (
              <DatePicker
                value={data.value}
                onChange={(value) =>
                  patch({
                    value: value ? Dayjs(value).format('YYYY-MM-DD') : null
                  })
                }
                views={['year', 'month', 'day']}
              />
            ) : null}
          </>
        )}

        <Box sx={{ mt: 0.5 }}>
          <Caption>Enhet</Caption>
        </Box>
        <FunctionSelect
          value={data.unit}
          options={TIME_UNITS}
          onChange={(unit) => patch({ unit: unit as TimeUnit })}
          sx={{ width: '100%' }}
        />
      </Box>
    </Node>
  )
}

export default TimeDifference
